import {
  PlayerSnapshot, WeaponDef, WeaponSlot,
} from '../shared/types';
import { WEAPON_DEFINITIONS } from '../shared/constants';

export interface WeaponState {
  currentWeapon: WeaponSlot;
  ammo: number;
  reserve: number;
  reloading: boolean;
  reloadTimer: number;
  fireTimer: number;
  switching: boolean;
  switchTimer: number;
  recoilOffset: number;
}

export class WeaponSystem {
  private weapons: Record<WeaponSlot, WeaponDef>;
  private state: WeaponState;

  constructor() {
    this.weapons = {
      primary: WEAPON_DEFINITIONS.rifle as WeaponDef,
      secondary: WEAPON_DEFINITIONS.pistol as WeaponDef,
      melee: WEAPON_DEFINITIONS.melee as WeaponDef,
      grenade: WEAPON_DEFINITIONS.pistol as WeaponDef,
    };
    this.state = {
      currentWeapon: 'primary',
      ammo: WEAPON_DEFINITIONS.rifle.magazineSize,
      reserve: WEAPON_DEFINITIONS.rifle.maxAmmo,
      reloading: false,
      reloadTimer: 0,
      fireTimer: 0,
      switching: false,
      switchTimer: 0,
      recoilOffset: 0,
    };
  }

  getState(): WeaponState {
    return { ...this.state };
  }

  getCurrentWeapon(): WeaponDef {
    return this.weapons[this.state.currentWeapon];
  }

  switchWeapon(slot: WeaponSlot): void {
    if (slot === this.state.currentWeapon || this.state.switching) return;
    this.state.switching = true;
    this.state.switchTimer = 0.3;
    this.state.reloading = false;
    this.state.reloadTimer = 0;
  }

  canFire(): boolean {
    const weapon = this.getCurrentWeapon();
    return !this.state.reloading &&
           !this.state.switching &&
           this.state.fireTimer <= 0 &&
           this.state.ammo > 0;
  }

  fire(): { damage: number; spread: number; recoil: number } | null {
    if (!this.canFire()) return null;
    const weapon = this.getCurrentWeapon();
    this.state.ammo--;
    this.state.fireTimer = weapon.fireRate;
    this.state.recoilOffset = weapon.recoil;

    if (weapon.slot === 'melee') {
      return { damage: weapon.damage, spread: 0, recoil: 0 };
    }

    const spreadX = (Math.random() - 0.5) * weapon.spread * 2;
    const spreadY = (Math.random() - 0.5) * weapon.spread * 2;
    return {
      damage: weapon.damage,
      spread: Math.sqrt(spreadX * spreadX + spreadY * spreadY),
      recoil: weapon.recoil,
    };
  }

  startReload(): boolean {
    const weapon = this.getCurrentWeapon();
    if (this.state.reloading || this.state.switching) return false;
    if (this.state.ammo >= weapon.magazineSize) return false;
    if (this.state.reserve <= 0 && weapon.slot !== 'melee') return false;
    this.state.reloading = true;
    this.state.reloadTimer = weapon.reloadTime;
    return true;
  }

  switchToSlot(slot: WeaponSlot): void {
    if (slot === this.state.currentWeapon) return;
    this.state.currentWeapon = slot;
    const weapon = this.weapons[slot];
    this.state.ammo = weapon.magazineSize;
    this.state.reserve = weapon.maxAmmo;
    this.state.reloading = false;
    this.state.reloadTimer = 0;
  }

  update(dt: number): void {
    this.state.fireTimer = Math.max(0, this.state.fireTimer - dt);
    this.state.recoilOffset *= Math.max(0, 1 - dt * 12);

    if (this.state.reloading) {
      this.state.reloadTimer -= dt;
      if (this.state.reloadTimer <= 0) {
        const weapon = this.getCurrentWeapon();
        const needed = weapon.magazineSize - this.state.ammo;
        const available = Math.min(needed, this.state.reserve);
        this.state.ammo += available;
        if (weapon.slot !== 'melee') {
          this.state.reserve -= available;
        }
        this.state.reloading = false;
        this.state.reloadTimer = 0;
      }
    }

    if (this.state.switching) {
      this.state.switchTimer -= dt;
      if (this.state.switchTimer <= 0) {
        this.state.switching = false;
        this.state.switchTimer = 0;
      }
    }
  }

  equipWeapon(slot: WeaponSlot, weaponId: string): void {
    const def = WEAPON_DEFINITIONS[weaponId as keyof typeof WEAPON_DEFINITIONS];
    if (def) {
      this.weapons[slot] = { ...def, slot } as WeaponDef;
    }
  }
}
